import { useCallback, useEffect, useId, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PageHeader } from '@/deploy/components/layout/PageHeader';
import { Icon } from '@/deploy/components/ui/Icon';
import { Button } from '@/deploy/components/ui/Button';
import { Badge } from '@/deploy/components/ui/Badge';
import { PermissionLock } from '@/deploy/components/ui/PermissionLock';
import { useSessionStore } from '@/deploy/stores/session.store';
import {
  promotionService, type PromoteStepResult, type PromotionActionResult, type PromotionJobSnapshot,
} from '@/deploy/services/promotion.service';
import { procedureService, STEP_TYPE_LABELS, type UpdateProcedure } from '@/deploy/services/procedure.service';
import { ProcedureEditorModal } from './Settings';
import { formatRelative } from '@/deploy/utils/format';
import { can } from '@/deploy/utils/permissions';
import { outcomeBadge, phaseTag, proxyBanner, type BannerTone } from './promotion.report';
import styles from './DeployProgress.module.scss';

// ============================================================
// Page /deploiement/deployments — Suivi PARTAGÉ des promotions (Pré-prod → Prod).
// Le job tourne côté backend (async). L'onglet « En cours » lit les jobs en mémoire
// (GET /promote/jobs, fenêtre 30 min) avec auto-refresh ; l'onglet « Historique » lit
// l'historique PERSISTANT (GET /promote/history, paginé : qui a lancé, quand, quoi).
// Visible par tous (cicd.view), remplace la popup bloquante : lancer une promotion redirige ici.
// ============================================================

const POLL_ACTIVE_MS = 3_000;
const HISTORY_PAGE_SIZE = 25;
const POLL_IDLE_MS = 8_000;
const ENV_LABEL: Record<string, string> = { dev: 'Dev', beta: 'Bêta', preprod: 'Pré-prod', prod: 'Prod' };

function jobState(j: PromotionJobSnapshot): { label: string; tone: 'info' | 'success' | 'danger' | 'warning'; active: boolean } {
  // Promotion d'un bloc : le badge dit comment elle a fini (refusée, annulée, proxy arrêté...)
  const badge = outcomeBadge(j);
  if (badge) {
    const anyKo = (j.results ?? []).some((r) => !r.ok);
    // Fin normale mais une étape rouge (ex. sans le modèle des lobbies) : pas de « ✓ »
    if (j.outcome === 'ok' && anyKo) return { label: 'Terminé avec erreurs', tone: 'danger', active: false };
    return { ...badge, active: false };
  }
  if (!j.done) {
    // currentStepIndex vaut -1 pendant l'init côté backend → on clampe entre 1 et total pour
    // ne jamais afficher « étape 0 » ni « étape N+1/N ».
    const total = j.totalSteps || j.results.length || 0;
    const stepNo = total ? Math.min(Math.max(j.currentStepIndex + 1, 1), total) : 0;
    return {
      label: total ? `En cours — étape ${stepNo}/${total}` : 'En cours — initialisation…',
      tone: 'info', active: true,
    };
  }
  const anyKo = (j.results ?? []).some((r) => !r.ok) || !!j.error;
  return anyKo
    ? { label: 'Terminé avec erreurs', tone: 'danger', active: false }
    : { label: 'Terminé ✓', tone: 'success', active: false };
}

export function PromotionProgress() {
  const navigate = useNavigate();
  const project = useSessionStore((s) => s.currentProject());
  const [jobs, setJobs] = useState<PromotionJobSnapshot[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tab, setTab] = useState<'active' | 'history' | 'settings'>('active');
  const [procOpen, setProcOpen] = useState(false);
  const [procReload, setProcReload] = useState(0); // bump à la fermeture de l'éditeur → recharge la liste
  // Historique persistant (survit à la fenêtre 30 min et aux redémarrages de l'API).
  const [history, setHistory] = useState<PromotionJobSnapshot[]>([]);
  const [historyTotal, setHistoryTotal] = useState(0);
  const [historyPage, setHistoryPage] = useState(0);
  const [historyLoading, setHistoryLoading] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const list = await promotionService.listJobs(project.id);
      setJobs(list);
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Erreur');
    } finally {
      setLoading(false);
    }
  }, [project.id]);

  // Reset + 1er chargement au montage / changement de projet (n'affiche pas les jobs de l'ancien projet).
  useEffect(() => { setJobs([]); setLoading(true); void refresh(); }, [refresh]);

  const loadHistory = useCallback(async (page: number, append: boolean) => {
    if (!project.id) return;
    setHistoryLoading(true);
    try {
      const res = await promotionService.history(project.id, page, HISTORY_PAGE_SIZE);
      setHistory((prev) => (append ? [...prev, ...res.items] : res.items));
      setHistoryTotal(res.total);
      setHistoryPage(page);
    } catch {
      // best-effort : on garde la liste déjà affichée
    } finally {
      setHistoryLoading(false);
    }
  }, [project.id]);

  useEffect(() => { setHistory([]); setHistoryTotal(0); setHistoryPage(0); }, [project.id]);

  // Première page chargée quand on ouvre l'onglet (et non au montage : inutile si on ne le regarde pas).
  useEffect(() => {
    if (tab !== 'history' || history.length > 0) return;
    void loadHistory(0, false);
  }, [tab, history.length, loadHistory]);

  // Auto-refresh ADAPTATIF : 3s tant qu'un job est actif, 8s au repos (le board est partagé →
  // on continue de guetter une promotion lancée par un collègue, mais moins souvent quand rien ne bouge).
  const jobsRef = useRef(jobs);
  jobsRef.current = jobs;
  useEffect(() => {
    let alive = true;
    let t: ReturnType<typeof setTimeout> | null = null;
    const tick = async () => {
      await refresh();
      if (!alive) return;
      const anyActive = jobsRef.current.some((j) => !j.done);
      t = setTimeout(() => void tick(), anyActive ? POLL_ACTIVE_MS : POLL_IDLE_MS);
    };
    t = setTimeout(() => void tick(), POLL_ACTIVE_MS);
    return () => { alive = false; if (t) clearTimeout(t); };
  }, [refresh]);

  const activeJobs = jobs.filter((j) => !j.done);
  const failedCount = history.filter((j) => (j.results ?? []).some((r) => !r.ok) || j.error).length;
  const okCount = history.length - failedCount;

  const TABS = [
    { id: 'active' as const, label: 'En cours', icon: 'refresh' as const, count: activeJobs.length },
    { id: 'history' as const, label: 'Historique', icon: 'history' as const, count: historyTotal },
    { id: 'settings' as const, label: 'Settings', icon: 'settings' as const, count: undefined },
  ];

  return (
    <div className={styles.page}>
      <PageHeader
        eyebrow="Suivi déploiement"
        title="Déploiements"
        subtitle="Suivi en temps réel des promotions (Pré-prod → Prod). Visible par tous. L'onglet Historique conserve la trace de qui a lancé quoi, bien au-delà des 30 min du suivi live."
        actions={
          tab === 'settings' ? undefined : (
            <div className={styles.headerActions}>
              <Button variant="ghost" size="sm" leftIcon={<Icon name="refresh" size={12} />} onClick={() => void refresh()}>
                Rafraîchir
              </Button>
              <Button variant="primary" size="sm" leftIcon={<Icon name="rocket" size={12} />}
                onClick={() => navigate('/deploiement/environments/preprod')}>
                Nouvelle promotion
              </Button>
            </div>
          )
        }
      />

      <section className={styles.summary}>
        <div className={styles.summaryItem}>
          <span className={[styles.num, activeJobs.length > 0 && styles.numHl].filter(Boolean).join(' ')}>{activeJobs.length}</span>
          <em>en cours</em>
        </div>
        <div className={styles.summaryItem}>
          <span className={[styles.num, okCount > 0 && styles.numOk].filter(Boolean).join(' ')}>{okCount}</span>
          <em>terminés</em>
        </div>
        <div className={styles.summaryItem}>
          <span className={[styles.num, failedCount > 0 && styles.numKo].filter(Boolean).join(' ')}>{failedCount}</span>
          <em>avec erreurs</em>
        </div>
      </section>

      {/* Mini-navigation : En cours / Historique / Settings */}
      <nav className={styles.tabs}>
        {TABS.map((t) => (
          <button key={t.id} type="button"
            className={[styles.tab, tab === t.id && styles.tabActive].filter(Boolean).join(' ')}
            onClick={() => setTab(t.id)}>
            <Icon name={t.icon} size={13} />
            <span>{t.label}</span>
            {typeof t.count === 'number' && <span className={styles.count}>{t.count}</span>}
          </button>
        ))}
      </nav>

      {tab === 'settings' ? (
        <ProceduresSettings projectId={project.id} reloadKey={procReload} onEdit={() => setProcOpen(true)} />
      ) : (
        <>
          {/* Erreur de rafraîchissement NON bloquante : on garde les dernières données affichées. */}
          {error && jobs.length > 0 && (
            <div style={{
              margin: '0 0 12px', padding: '8px 12px', borderRadius: 8, fontSize: 12,
              background: 'var(--color-danger-bg)', color: 'var(--color-danger)',
              display: 'flex', gap: 8, alignItems: 'center',
            }}>
              <Icon name="warn" size={12} /> Rafraîchissement en erreur ({error}) — dernières données affichées.
            </div>
          )}

          {tab === 'active' ? (
            activeJobs.length > 0 ? (
              <div className={styles.cardList}>
                {activeJobs.map((j) => <JobCard key={j.jobId} job={j} />)}
              </div>
            ) : loading && jobs.length === 0 ? (
              <div className={styles.empty}><p>Chargement…</p></div>
            ) : error && jobs.length === 0 ? (
              <div className={styles.empty}><Icon name="warn" size={28} /><p>{error}</p></div>
            ) : (
              <div className={styles.empty}>
                <Icon name="rocket" size={32} />
                <p>Aucun déploiement en cours.</p>
                <Button variant="primary" leftIcon={<Icon name="rocket" size={12} />}
                  onClick={() => navigate('/deploiement/environments/preprod')}>
                  Lancer une promotion
                </Button>
              </div>
            )
          ) : (
            <PromotionHistory
              jobs={history}
              total={historyTotal}
              loading={historyLoading}
              onMore={() => void loadHistory(historyPage + 1, true)}
            />
          )}
        </>
      )}

      {procOpen && (
        <ProcedureEditorModal projectId={project.id} projectName={project.name}
          onClose={() => { setProcOpen(false); setProcReload((n) => n + 1); }} />
      )}
    </div>
  );
}

// ---------- Onglet Settings : liste des procédures prod + éditeur (réutilise ProcedureEditorModal) ----------
function ProceduresSettings({ projectId, reloadKey, onEdit }: { projectId: string; reloadKey: number; onEdit: () => void }) {
  const [procs, setProcs] = useState<Record<string, UpdateProcedure> | null>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    void (async () => {
      try {
        const all = await procedureService.listByName(projectId, 'prod');
        if (alive) { setProcs(all); setErr(null); }
      } catch (e) {
        if (alive) setErr(e instanceof Error ? e.message : 'Erreur');
      }
    })();
    return () => { alive = false; };
  }, [projectId, reloadKey]);

  const names = procs ? Object.keys(procs) : [];

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12, flexWrap: 'wrap' }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 600, fontSize: 14, color: 'var(--color-text)' }}>Procédures de déploiement (prod)</div>
          <div style={{ fontSize: 12, color: 'var(--color-text-muted)' }}>
            La procédure choisie au lancement définit les étapes exécutées lors de la promotion Pré-prod → Prod.
          </div>
        </div>
        <Button variant="primary" size="sm" leftIcon={<Icon name="settings" size={12} />} onClick={onEdit}>
          Éditer / créer
        </Button>
      </div>

      {err && <div className={styles.empty}><Icon name="warn" size={22} /><p>{err}</p></div>}
      {!err && !procs && <div className={styles.empty}><p>Chargement…</p></div>}
      {!err && procs && names.length === 0 && (
        <div className={styles.empty}>
          <Icon name="settings" size={28} />
          <p>Aucune procédure prod configurée.</p>
          <Button variant="primary" size="sm" leftIcon={<Icon name="plus" size={12} />} onClick={onEdit}>Créer une procédure</Button>
        </div>
      )}
      {!err && procs && names.length > 0 && (
        <div className={styles.procList}>
          {names.map((name) => {
            const proc = procs[name];
            return (
              <div key={name} className={styles.procCard}>
                <div className={styles.procMain}>
                  <div className={styles.procName}>
                    {name}
                    <span style={{ fontSize: 12, color: 'var(--color-text-muted)', fontWeight: 400 }}>
                      {' '}· {proc.steps.length} étape{proc.steps.length > 1 ? 's' : ''}
                    </span>
                  </div>
                  <div className={styles.procSteps}>
                    {proc.steps.length === 0 && <span style={{ fontSize: 12, color: 'var(--color-text-muted)' }}>(vide)</span>}
                    {proc.steps.map((s, i) => (
                      <span key={i} className={styles.procStepPill} title={STEP_TYPE_LABELS[s.type] ?? s.type}>
                        {s.label || STEP_TYPE_LABELS[s.type] || s.type}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// Une étape du job : la ligne, l'erreur en clair dessous quand elle échoue, et un dépliant avec
// les messages renvoyés par le CICD (warnings : packs KO, serveurs sautés, eggs...) et les serveurs
// concernés. Une étape en échec s'ouvre dépliée : sa cause est souvent dans ces messages.
function StepRow({ step: r }: { step: PromoteStepResult }) {
  const warnings = r.warnings ?? [];
  const names = r.matchedServerNames ?? [];
  const hasDetails = warnings.length > 0 || names.length > 0;
  const [open, setOpen] = useState(!r.ok);
  const detailsId = useId();
  // update-apis recopie son libellé enrichi dans error : déjà lisible sur la ligne, pas de doublon.
  const errorText = r.ok ? null
    : !r.error ? 'Aucun message d\'erreur enregistré pour cette étape.'
    : r.error === r.label ? null
    : r.error;
  const toggleLabel = warnings.length > 0
    ? `${warnings.length} détail${warnings.length > 1 ? 's' : ''}`
    : 'Serveurs';
  const tag = phaseTag(r.phase);

  return (
    <div className={[styles.promoStep, r.ok ? styles.promoStepOk : styles.promoStepKo].join(' ')}>
      <div className={styles.promoStepLine}>
        <span className={styles.promoStepHead}>
          <span style={{ color: r.ok ? 'var(--color-success)' : 'var(--color-danger)', display: 'inline-flex' }}>
            <Icon name={r.ok ? 'check' : 'warn'} size={12} />
          </span>
          <strong style={{ color: 'var(--color-text)' }}>{r.stepIndex + 1}.</strong>
          {tag && <span className={styles.promoStepPhase}>{tag}</span>}
          <span className={styles.promoStepLabel}>{r.label}</span>
        </span>
        <span className={styles.promoStepAside}>
          {hasDetails && (
            <button type="button"
              className={[styles.promoStepToggle, !r.ok && styles.promoStepToggleKo].filter(Boolean).join(' ')}
              aria-expanded={open} aria-controls={open ? detailsId : undefined}
              title={open ? 'Masquer les détails de l\'étape' : 'Afficher les détails de l\'étape'}
              onClick={() => setOpen((o) => !o)}>
              <Icon name={open ? 'chevron-down' : 'chevron-right'} size={11} />
              {toggleLabel}
            </button>
          )}
          {typeof r.matchedServers === 'number' && (
            <span className={styles.promoStepMeta}>{r.matchedServers} srv</span>
          )}
          <span className={styles.promoStepMeta}>{r.durationMs}ms</span>
        </span>
      </div>

      {errorText && <div className={styles.promoStepError}>{errorText}</div>}

      {hasDetails && open && (
        <div id={detailsId} className={styles.promoStepDetails}>
          {warnings.length > 0 && (
            <section>
              <div className={styles.promoStepDetailsTitle}>Détails ({warnings.length})</div>
              <ul className={styles.promoStepWarnings}>
                {warnings.map((w, i) => <li key={i}>{w}</li>)}
              </ul>
            </section>
          )}
          {names.length > 0 && (
            <section>
              <div className={styles.promoStepDetailsTitle}>Serveurs concernés ({names.length})</div>
              <div className={styles.promoStepServers}>
                {names.map((n, i) => <code key={i} className={styles.promoStepServer}>{n}</code>)}
              </div>
            </section>
          )}
        </div>
      )}
    </div>
  );
}

const BANNER_ICON: Record<BannerTone, 'check' | 'clock' | 'warn'> = {
  success: 'check', info: 'clock', warning: 'warn', danger: 'warn',
};

// Bandeau de la promotion d'un bloc : où en est le proxy, le bilan, quoi faire, et les gestes
// « Démarrer le proxy » (promotion arrêtée, proxy laissé arrêté) et « Aligner l'egg sur les serveurs ».
function ProxyBannerBox({ job }: { job: PromotionJobSnapshot }) {
  const user = useSessionStore((s) => s.user);
  const [busy, setBusy] = useState<'proxy' | 'eggs' | null>(null);
  const [action, setAction] = useState<PromotionActionResult | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const banner = proxyBanner(job);
  if (!banner) return null;
  const allowed = can(user, 'cicd.deploy.prod');

  const run = async (kind: 'proxy' | 'eggs') => {
    const question = kind === 'proxy'
      ? (job.outcome === 'halted' || job.outcome === 'interrupted'
        ? 'La mise à jour ne s\'est pas terminée : si les serveurs de jeu n\'ont pas tous la même version, les joueurs'
          + ' verront des objets décalés. Démarrer le proxy quand même ?'
        : 'Démarrer le proxy maintenant ?')
      : 'Chaque egg resté derrière passera sur la version de ses serveurs : Nexus créera ses prochains lobbies dessus.'
        + ' Continuer ?';
    if (!confirm(question)) return;
    setBusy(kind);
    setAction(null);
    setActionError(null);
    try {
      setAction(kind === 'proxy'
        ? await promotionService.startProxies(job.projectId, job.targetEnv)
        : await promotionService.alignEggs(job.projectId, job.targetEnv));
    } catch (e) {
      setActionError(e instanceof Error ? e.message : 'Erreur inconnue');
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className={[styles.proxyBanner, styles[`proxyBanner_${banner.tone}`]].join(' ')}
      role={banner.tone === 'danger' ? 'alert' : 'status'}>
      <div className={styles.proxyBannerTitle}>
        <Icon name={BANNER_ICON[banner.tone]} size={14} />
        <strong>{banner.title}</strong>
      </div>
      {banner.lines.length > 0 && (
        <ul className={styles.proxyBannerLines}>
          {banner.lines.map((l, i) => <li key={i}>{l}</li>)}
        </ul>
      )}
      {banner.notRun.length > 0 && (
        <div className={styles.proxyBannerNotRun}>Étapes non jouées : {banner.notRun.join(' · ')}</div>
      )}
      {(banner.canStartProxy || banner.canAlignEggs) && (
        <div className={styles.proxyBannerActions}>
          {banner.canStartProxy && (
            <Button size="sm" variant="primary" leftIcon={<Icon name="play" size={12} />}
              onClick={() => void run('proxy')} disabled={!allowed || busy !== null} loading={busy === 'proxy'}>
              Démarrer le proxy
              {!allowed && <PermissionLock inline />}
            </Button>
          )}
          {banner.canAlignEggs && (
            <Button size="sm" variant="secondary" leftIcon={<Icon name="layers" size={12} />}
              onClick={() => void run('eggs')} disabled={!allowed || busy !== null} loading={busy === 'eggs'}>
              Aligner l'egg sur les serveurs
              {!allowed && <PermissionLock inline />}
            </Button>
          )}
        </div>
      )}
      {action && (
        <div className={action.ok ? styles.proxyBannerDone : styles.proxyBannerFailed}>
          <strong>{action.summary}</strong>
          {action.lines.length > 0 && (
            <ul className={styles.proxyBannerLines}>
              {action.lines.map((l, i) => <li key={i}>{l}</li>)}
            </ul>
          )}
        </div>
      )}
      {actionError && <div className={styles.proxyBannerFailed}>{actionError}</div>}
    </div>
  );
}

function JobCard({ job }: { job: PromotionJobSnapshot }) {
  const st = jobState(job);
  const results = job.results ?? [];
  return (
    <article className={[
      styles.card,
      st.tone === 'success' && styles.cardOk,
      st.tone === 'danger' && styles.cardKo,
      st.active && styles.cardActive,
    ].filter(Boolean).join(' ')}>
      <header className={styles.cardHeader}>
        <span className={styles.chip} style={{ background: 'var(--color-primary)' }}>
          <Icon name="rocket" size={14} />
        </span>
        <div className={styles.cardTitle}>
          <strong>{ENV_LABEL[job.sourceEnv] ?? job.sourceEnv} → {ENV_LABEL[job.targetEnv] ?? job.targetEnv}</strong>
          <code className={styles.branch}>{job.jobId}</code>
        </div>
        <Badge tone={st.tone}>
          {st.active && <span className={styles.pulse} />}
          {st.label}
        </Badge>
      </header>

      <ProxyBannerBox job={job} />

      <div style={{ display: 'flex', flexDirection: 'column', gap: 4, padding: '4px 2px' }}>
        {results.length === 0 && st.active && (
          <div style={{ fontSize: 12, color: 'var(--color-text-muted)', display: 'flex', gap: 8, alignItems: 'center', padding: '4px 8px' }}>
            <span className={styles.spinner} /> Démarrage…
          </div>
        )}
        {results.map((r) => <StepRow key={r.stepIndex} step={r} />)}
        {st.active && results.length > 0 && (() => {
          const total = job.totalSteps || results.length;
          const stepNo = Math.min(Math.max(job.currentStepIndex + 1, 1), total);
          return (
            <div style={{ fontSize: 12, color: 'var(--color-info)', display: 'flex', gap: 8, alignItems: 'center', padding: '4px 8px' }}>
              <span className={styles.spinner} /> Étape {stepNo}/{total} en cours…
            </div>
          );
        })()}
        {job.error && (
          <div style={{ fontSize: 12, color: 'var(--color-danger)', padding: '6px 8px', display: 'flex', gap: 6, alignItems: 'flex-start' }}>
            <Icon name="warn" size={12} /> {job.error}
          </div>
        )}
      </div>

      <footer className={styles.cardFooter}>
        <span>Démarré {formatRelative(job.startedAt)}</span>
        {job.endedAt && <span>· Terminé {formatRelative(job.endedAt)}</span>}
      </footer>
    </article>
  );
}

// ============================================================
// Historique persistant : la LISTE (qui / quand / quoi), détail au clic
// ============================================================
// Les jobs en mémoire disparaissent au bout de 30 min (et au redémarrage de l'API). Cette liste vient
// de la collection Mongo : elle garde la trace de chaque promotion, avec son auteur.
function PromotionHistory({
  jobs, total, loading, onMore,
}: {
  jobs: PromotionJobSnapshot[];
  total: number;
  loading: boolean;
  onMore: () => void;
}) {
  const [openId, setOpenId] = useState<string | null>(null);

  if (jobs.length === 0) {
    return (
      <div className={styles.empty}>
        <Icon name="history" size={32} />
        <p>{loading ? 'Chargement de l\'historique…' : 'Aucune promotion enregistrée sur ce projet.'}</p>
      </div>
    );
  }

  return (
    <>
      <div className={styles.histTable}>
        <div className={[styles.histRow, styles.histHead].join(' ')}>
          <span>Quoi</span>
          <span>Procédure</span>
          <span>Qui</span>
          <span>Quand</span>
          <span>Statut</span>
          <span />
        </div>
        {jobs.map((j) => {
          const st = jobState(j);
          const open = openId === j.jobId;
          const ko = (j.results ?? []).filter((r) => !r.ok).length;
          return (
            <div key={j.jobId} className={styles.histGroup}>
              <div className={styles.histRow}>
                <span className={styles.histWhat}>
                  <i className={styles.histDot} style={{ background: 'var(--color-primary)' }} />
                  <strong>{ENV_LABEL[j.sourceEnv] ?? j.sourceEnv} → {ENV_LABEL[j.targetEnv] ?? j.targetEnv}</strong>
                </span>
                <span className={styles.histVersion}>
                  {(j.results ?? []).length} étape(s){ko > 0 ? ` · ${ko} en échec` : ''}{j.dryRun ? ' · dry-run' : ''}
                </span>
                <span className={styles.histWho}>{j.actorName || j.actorId || '—'}</span>
                <span className={styles.histWhen} title={new Date(j.startedAt).toLocaleString('fr-FR')}>
                  {formatRelative(j.startedAt)}
                </span>
                <span><Badge tone={st.tone}>{st.label}</Badge></span>
                <span className={styles.histActions}>
                  <Button variant="ghost" size="sm" onClick={() => setOpenId(open ? null : j.jobId)}>
                    {open ? 'Masquer' : 'Détail'}
                  </Button>
                </span>
              </div>
              {open && (
                <div className={styles.histDetail}>
                  <JobCard job={j} />
                </div>
              )}
            </div>
          );
        })}
      </div>
      {jobs.length < total && (
        <div className={styles.histMore}>
          <Button variant="ghost" size="sm" loading={loading} onClick={onMore}>
            Charger plus ({jobs.length} / {total})
          </Button>
        </div>
      )}
    </>
  );
}
